export type RecoveryIntent =
  | { kind: "token"; tokenHash: string; returnTo: string | null }
  | { kind: "invalid" }
  | { kind: "none" };

const TOKEN_HASH_RE = /^[A-Za-z0-9_-]{20,512}$/;

export function safeRecoveryReturnTo(value: string | null): string | null {
  if (!value || !value.startsWith("/radar") || value.startsWith("//")) return null;
  try {
    const parsed = new URL(value, "https://bdse.local");
    if (parsed.origin !== "https://bdse.local" || parsed.pathname !== "/radar") return null;
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

function parameters(value: string, prefix: "?" | "#"): URLSearchParams {
  return new URLSearchParams(value.startsWith(prefix) ? value.slice(1) : value);
}

/**
 * Read a scanner-resistant password-recovery intent from either the URL
 * fragment (preferred, because fragments are not sent to web servers) or the
 * query string (supported for backwards-compatible links).
 */
export function parseRecoveryIntent(search: string, hash: string): RecoveryIntent {
  const sources = [parameters(hash, "#"), parameters(search, "?")];

  for (const source of sources) {
    if (source.has("error") || source.has("error_code") || source.has("error_description")) {
      return { kind: "invalid" };
    }
    const tokenHash = source.get("token_hash");
    if (tokenHash !== null) {
      if (source.get("type") !== "recovery" || !TOKEN_HASH_RE.test(tokenHash)) {
        return { kind: "invalid" };
      }
      return {
        kind: "token",
        tokenHash,
        returnTo: safeRecoveryReturnTo(source.get("return_to")),
      };
    }
  }

  return { kind: "none" };
}

/** Remove recovery credentials and provider errors from the visible URL. */
export function clearRecoveryLocation(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}
